import * as React from 'react';
import { createCtx } from '../utils/createCtx';
import { IsSsrContextType } from '../types';
import type { FlyoutRenderMode } from '../types/FlyoutRenderMode';

export const [useIsSsrContext, IsSsrContext] = createCtx<IsSsrContextType>();

export const IsSsrProvider: React.FC<{
  children: React.ReactNode;
  flyoutRenderMode?: FlyoutRenderMode;
  /**
   * For testing only - allows tests to control SSR state
   * If set to false, prevents auto-hydration in useEffect
   */
  initialIsSsr?: boolean;
}> = (props) => {
  const [isSsr, setIsSsr] = React.useState<boolean>(props.initialIsSsr ?? true);
  const [hoveredFlyouts, setHoveredFlyouts] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    // Only auto-hydrate if initialIsSsr is undefined (normal behavior)
    // If explicitly set to true or false, keep that state (for testing)
    if (props.initialIsSsr === undefined) {
      setIsSsr(false);
    }
  }, [props.initialIsSsr]);

  const markFlyoutHovered = React.useCallback((flyoutId: string) => {
    setHoveredFlyouts((prev) => {
      if (prev.has(flyoutId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(flyoutId);
      return next;
    });
  }, []);

  const isFlyoutHovered = React.useCallback(
    (flyoutId: string) => hoveredFlyouts.has(flyoutId),
    [hoveredFlyouts]
  );

  return (
    <IsSsrContext
      value={{
        isSsr,
        flyoutRenderMode: props.flyoutRenderMode || 'default',
        markFlyoutHovered,
        isFlyoutHovered,
      }}
    >
      {props.children}
    </IsSsrContext>
  );
};
